import React, { useMemo, useState } from 'react';
import { SearchIcon } from 'lucide-react';
import { useData } from '../../store/DataContext';

/** Read-only audit trail of every stock disposal. */
export function DisposalHistory() {
  const {
    stockDisposals
  } = useData();
  const [search, setSearch] = useState('');
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...stockDisposals].sort((a, b) => b.disposedAt.localeCompare(a.disposedAt)).filter(d => !q || [d.medicineName, d.batchNumber, d.disposalType, d.receivingFacility, d.disposedByName, d.reason].filter(Boolean).some(value => String(value).toLowerCase().includes(q)));
  }, [stockDisposals, search]);
  return <section aria-labelledby="disposal-history-heading" className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-8667" data-mp="jeyrens7dckr">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4" data-id="element-8668" data-mp="3n15v9agdek60">
        <div data-id="element-8669" data-mp="2mszweldu0oy0">
          <h3 id="disposal-history-heading" className="font-bold text-slate-900" data-id="element-8670" data-mp="2msq3aoxhq1o3">
            Disposal history
          </h3>
          <p className="mt-0.5 text-sm text-slate-500" data-id="element-8671" data-mp="2d3q35279508b">
            Permanent record of every disposal for audit and reconciliation.
          </p>
        </div>
        <div className="relative w-full sm:w-64" data-id="element-8672" data-mp="2843qatvc5m01">
          <label htmlFor="disposal-search" className="sr-only" data-id="element-8673" data-mp="235af583iu731">
            Search disposal history
          </label>
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" data-id="element-8674" data-mp="1z3p1vln2rqsb" />
          <input id="disposal-search" type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search drug, batch, facility…" className="block w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500" data-id="element-8675" data-mp="1u5421nbqj94e" />
        </div>
      </div>
      <div className="overflow-x-auto" data-id="element-8676" data-mp="1hsrvlbz8tiue">
        <table className="min-w-full divide-y divide-slate-100 text-sm" data-id="element-8677" data-mp="1mll50d5npfct">
          <thead className="bg-slate-50" data-id="element-8678" data-mp="17woxqibfc6d9">
            <tr data-id="element-8679" data-mp="17xrp0rcnsatp">
              {['Date & time', 'Item / batch', 'Qty', 'Method', 'Reason', 'Stock before → after', 'Disposed by'].map(heading => <th key={heading} scope="col" className="whitespace-nowrap px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-8680" data-mp="20ldnxmaxyra8">
                  {heading}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-8681" data-mp="1vl81qlnfxwzj">
            {rows.length ? rows.map(d => <tr key={d.id} data-id="element-8682" data-mp="f67xmk5wrqgm">
                  <td className="whitespace-nowrap px-5 py-3 text-slate-600" data-id="element-8683" data-mp="fa44tdfavm7l">
                    {new Date(d.disposedAt).toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
                  </td>
                  <td className="px-5 py-3" data-id="element-8684" data-mp="37d7dcyvqxzq0">
                    <p className="font-semibold text-slate-900" data-id="element-8685" data-mp="33dgjgmaxb3h9">{d.medicineName}</p>
                    <p className="text-xs text-slate-500" data-id="element-8686" data-mp="2fnvv1nw03e0l">Batch {d.batchNumber}</p>
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 font-semibold tabular-nums text-slate-900" data-id="element-8687" data-mp="2apkmrhlctbkh">
                    {d.quantity}
                  </td>
                  <td className="px-5 py-3" data-id="element-8688" data-mp="2oik2nkp1t7sk">
                    <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${d.disposalType === 'Donated To' ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'}`} data-id="element-8689" data-mp="2jph4ez9bta61">
                      {d.disposalType}
                    </span>
                    {d.receivingFacility && <p className="mt-1 text-xs text-slate-600" data-id="element-8690" data-mp="29mvakvfkj7e8">{d.receivingFacility}</p>}
                  </td>
                  <td className="px-5 py-3 text-slate-600" data-id="element-8691" data-mp="2eloo2nuuuyee">
                    {d.reason || '—'}
                    {d.notes && <p className="mt-0.5 text-xs text-slate-500" data-id="element-8692" data-mp="2jdq7quucrreg">{d.notes}</p>}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 tabular-nums text-slate-700" data-id="element-8693" data-mp="2oaq7yp4itzxn">
                    {d.stockBefore} → <span className="font-semibold text-slate-900" data-id="element-8694" data-mp="2sbs612mpc5y1">{d.stockAfter}</span>
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-slate-700" data-id="element-8695" data-mp="2xd95ctpjfkci">{d.disposedByName}</td>
                </tr>) : <tr data-id="element-8696" data-mp="12yyfchx7ik55">
                <td colSpan={7} className="px-5 py-10 text-center text-sm text-slate-500" data-id="element-8697" data-mp="12zgky55reapq">
                  {stockDisposals.length ? 'No disposals match your search.' : 'No disposals recorded yet.'}
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
    </section>;
}